import * as React from 'react';
import { Mutation } from 'react-apollo';
import { ApolloError } from 'apollo-client';
import { DataProxy } from 'apollo-cache';

import ToastActions from 'actions/ToastActions';
import BoxBody from 'components/BoxBody';
import Button from 'components/ButtonDeprecated';
import Checkbox from 'components/Checkbox';
import ConfirmDialog from 'components/ConfirmDialog';
import Dialog from 'components/Dialog';
import Link from 'components/Link';
import LinkConstants from 'constants/LinkConstants';

import { findMatchingRecord, findMatchingRecordIndex } from 'utils/arrayHelpers';

import ADMIN_SCHOOL_SIS_DASHBOARD_QUERY from 'admin/rostering/queries/adminSchoolSisDashboardQuery.gql';
import RESUME_ROSTERING_MUTATION from 'admin//rostering/mutations/ResumeRosteringMutation.gql';
import {
  AdminSchoolSisDashboard,
  AdminSchoolSisDashboard_organizations,
} from 'src/admin/rostering/queries/__generated__/AdminSchoolSisDashboard';
import {
  resumeRosteringVariables,
  resumeRostering_resumeRostering as resumeRosteringResult,
  resumeRostering as resumeRosteringData,
} from 'admin/rostering/mutations/__generated__/resumeRostering';

import './styles/AdminSisSyncHoldViewResumeModal.scss';

type Props = {
  permanentlyIgnoreable: boolean;
  temporarilyIgnored: boolean;
  orgUuid: string;
  holdKey: string;
  continueButtonText?: string;
  titleText: React.ReactNode;
  subtext: React.ReactNode;
  onRequestHide?: () => void;
};

type State = {
  permanentlyIgnore: boolean;
};

export default class AdminSisSyncHoldViewResumeModal extends React.Component<Props, State> {
  static defaultProps = {
    permanentlyIgnoreable: false,
    temporarilyIgnored: false,
  };

  state = {
    permanentlyIgnore: false,
  };

  handleComplete = (result: resumeRosteringResult) => {
    if (result.error) {
      const { fieldErrors } = result.error;

      const errorMessage =
        fieldErrors && fieldErrors.length
          ? result.error.fieldErrors.map(f => f.message).join(', ')
          : result.error.message;

      ToastActions.showErrorToast(errorMessage);
      console.debug(result.error);
      console.error('AdminSisSyncHoldViewResumeModal - could not resume');
    } else {
      ToastActions.showSuccessToast('Sync resumed');
    }
  };

  handleError = (error: ApolloError) => {
    ToastActions.showErrorToast('Oops, something went wrong resuming the sync');
    console.debug(error, error.stack);
    console.error('AdminSisSyncHoldViewResumeModal - hard fail - could not trigger resume');
  };

  onUpdate = (cache: DataProxy, { data }: { data?: resumeRosteringData }) => {
    const { orgUuid } = this.props;

    if (data && data.resumeRostering.error) {
      return;
    }

    const currentData: AdminSchoolSisDashboard | null = cache.readQuery({
      query: ADMIN_SCHOOL_SIS_DASHBOARD_QUERY,
      variables: { organizationUuid: [orgUuid] },
    });

    if (!currentData) {
      return;
    }

    const organization = findMatchingRecord<AdminSchoolSisDashboard_organizations>(
      currentData.organizations,
      orgUuid
    );

    if (organization && organization.sisSyncOverview) {
      organization.sisSyncOverview.syncStatus = 'processing';
      const index = findMatchingRecordIndex(currentData.organizations, orgUuid);

      if (index) {
        currentData.organizations[index] = organization;
        cache.writeQuery({ query: ADMIN_SCHOOL_SIS_DASHBOARD_QUERY, data: currentData });
      }
    }
  };

  render() {
    const {
      titleText,
      subtext,
      permanentlyIgnoreable,
      onRequestHide,
      temporarilyIgnored,
      holdKey,
      orgUuid,
      continueButtonText,
    } = this.props;

    const { permanentlyIgnore } = this.state;

    return (
      <Mutation
        mutation={RESUME_ROSTERING_MUTATION}
        onCompleted={this.handleComplete}
        onError={this.handleError}
        update={this.onUpdate}>
        {(resumeRostering: (input: { variables: resumeRosteringVariables }) => void) => (
          <ConfirmDialog
            title={titleText}
            subtext={subtext}
            onRequestHide={onRequestHide}
            divided={false}
            actionButtonText={continueButtonText ?? 'Yes, continue'}
            actionButtonColor={temporarilyIgnored ? 'apple' : 'mascot'}
            cancelButtonColor="mascot"
            cancelButtonLevel="text"
            cancelButtonText="Cancel"
            className="AdminSisSyncHoldViewResumeModal"
            preConfirmPromise={() =>
              Promise.resolve(
                resumeRostering({
                  variables: {
                    input: {
                      orgUuid,
                      skipPermanently: !!permanentlyIgnore,
                      skipUntilSuccessful: !!temporarilyIgnored,
                      holdKey,
                    },
                  },
                })
              )
            }>
            {permanentlyIgnoreable && (
              <div className="AdminSisSyncHoldViewResumeModal-checkbox">
                <Checkbox
                  flush
                  label="Don’t hold syncs like this again"
                  name="skip"
                  data-test-selector="permanently-ignore-checkbox"
                  checked={permanentlyIgnore}
                  onChange={() =>
                    this.setState({
                      permanentlyIgnore: !permanentlyIgnore,
                    })
                  }
                />
              </div>
            )}
          </ConfirmDialog>
        )}
      </Mutation>
    );
  }
}
